/**
 * Array utility functions for readonly/mutable conversions
 */
export class ArrayUtils {

    /**
   * Converts a readonly array to a mutable array
   * Creates a shallow copy to ensure the original array is not mutated
   *
   * @param readonlyArray - The readonly array to convert
   * @returns A mutable copy of the array
   */
    static toMutable<T>(readonlyArray: readonly T[]): T[] {
        return [...readonlyArray];
    }

    /**
   * Converts a readonly 2D array to a mutable 2D array
   * Creates shallow copies at both levels
   *
   * @param readonlyArray - The readonly 2D array to convert
   * @returns A mutable copy of the 2D array
   */
    static toMutable2D<T>(readonlyArray: readonly (readonly T[])[]): T[][] {
        return readonlyArray.map(inner => [...inner]);
    }

    /**
   * Compares two arrays for equality, ignoring the order of elements.
   * Useful for checking if two arrays contain the same set of values (e.g., primitives like strings or numbers).
   * Note: This method sorts both arrays to compare, so it works best with comparable types.
   *
   * @param arr1 - The first array to compare.
   * @param arr2 - The second array to compare.
   * @returns `true` if both arrays contain the same elements (regardless of order), otherwise `false`.
   */
    static areArraysEqual<T>(arr1: T[], arr2: T[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        const sorted1 = [...arr1].sort();
        const sorted2 = [...arr2].sort();
        return sorted1.every((value, index) => value === sorted2[index]);
    }
}
